// React
import React, { useEffect, useState, useRef, useCallback } from "react";
import PropTypes from "prop-types";
// Helpers
import { get, first, startsWith } from "@mefisto/utils";
// Framework
import { Box, Typography } from "ui";
import { ImageDialog } from "image";
import { Dropzone } from "storage";
// Components
import FormField from "../FormField";

////////////////////////////////////////////////////
/// Component
////////////////////////////////////////////////////

const Component = ({
  form: { touched, errors, setFieldValue },
  field: { name, value },
  accept = { "image/*": [], "video/*": [] },
  label,
  description,
  icon,
  aspect,
  closeable,
  display,
  dropzoneWidth = "100%",
  dropzoneHeight = "100%",
  maxWidth,
  maxHeight,
  minWidth,
  minHeight,
  zoneWidth,
  zoneHeight,
  borderRadius,
  onClose,
}) => {
  // Props
  const fieldError = get(errors, name);
  const showError = get(touched, name) && !!fieldError;
  // Ref
  const dialogRef = useRef(null);
  // State
  const [objectUrl, setObjectUrl] = useState(null);
  // Effects
  useEffect(() => {
    if (value === "") {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      setObjectUrl(null);
    }
  }, [value, objectUrl]);
  // Handlers
  const handleDrop = useCallback(
    (files) => {
      const file = first(files);
      if (file) {
        if (file.type === "image/gif" || startsWith(file.type, "video")) {
          setFieldValue(name, file);
          setObjectUrl(URL.createObjectURL(file));
        } else {
          // Let the user edit the file
          dialogRef.current.open(file);
        }
      }
    },
    [setFieldValue, name]
  );
  const handleFinished = useCallback(
    (file) => {
      setFieldValue(name, file);
      setObjectUrl(URL.createObjectURL(file));
    },
    [setFieldValue, name]
  );
  const handleClose = useCallback(() => {
    setFieldValue(name, "");
    setObjectUrl(null);
    onClose?.();
  }, [setFieldValue, name, onClose]);
  // Render
  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Box width={dropzoneWidth} height={dropzoneHeight}>
        <ImageDialog
          ref={dialogRef}
          aspect={aspect}
          display={display}
          maxWidth={maxWidth}
          minWidth={minWidth}
          maxHeight={maxHeight}
          minHeight={minHeight}
          zoneWidth={zoneWidth}
          zoneHeight={zoneHeight}
          onFinished={handleFinished}
        />
        <Dropzone
          closeable={closeable}
          accept={accept}
          image={objectUrl || get(value, "url")}
          video={objectUrl}
          label={label}
          description={description}
          icon={icon}
          error={showError}
          display={display}
          width={dropzoneWidth}
          height={dropzoneHeight}
          borderRadius={borderRadius}
          onDrop={handleDrop}
          onClose={handleClose}
        />
      </Box>
      {showError && (
        <Typography color="error" variant="caption">
          {fieldError}
        </Typography>
      )}
    </Box>
  );
};

const FormImageField = (props) => {
  return <FormField component={Component} {...props} />;
};

FormImageField.propTypes = {
  /**
   * Label for the dropzone
   * @type {string}
   */
  label: PropTypes.string,
  /**
   * Description for the dropzone
   * @type {string}
   */
  description: PropTypes.string,
  /**
   * Icon for the dropzone
   * @type {node}
   */
  icon: PropTypes.node,
  /**
   * Aspect ratio for the image
   * @type {number}
   */
  aspect: PropTypes.number,
  /**
   * Display style for the image
   * @type {"default" | "square" | "circle"}
   */
  display: PropTypes.oneOf(["default", "square", "circle"]),
  /**
   * Width of the dropzone
   * @type {number | string}
   */
  dropzoneWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * Height of the dropzone
   * @type {number | string}
   */
  dropzoneHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Maximum width of the image
   * @type {number}
   */
  maxWidth: PropTypes.number,
  /**
   * Maximum height of the image
   * @type {number}
   */
  maxHeight: PropTypes.number,
  /**
   * Minimum width of the image
   * @type {number}
   */
  minWidth: PropTypes.number,
  /**
   * Minimum height of the image
   * @type {number}
   */
  minHeight: PropTypes.number,
  /**
   * Border radius of the dropzone
   * @type {any}
   */
  borderRadius: PropTypes.any,
  /**
   * Callback function when the file is closed
   * @type {function}
   */
  onClose: PropTypes.func,
};

export default FormImageField;
